import { createContext, useContext } from 'react';

import { DESK_ONLY, type ConsoleAccess, type ConsolePreferences } from './preferences';

/**
 * The signed-in person's console choices, and the one way to change one.
 *
 * Apart from the provider for the reason `authContext.ts` is: `react/only-export-components`
 * warns on a module exporting both, and Fast Refresh cannot reload one that does.
 */
export interface Preferences {
  current: ConsolePreferences;
  /** Who they are, and whether the settings and the reports are theirs to open. */
  access: ConsoleAccess;
  /**
   * Remembers one or more choices for this person. Applied by the caller already — the
   * theme stamped, the language switched — this only makes them outlast the PC.
   */
  save: (patch: Partial<ConsolePreferences>) => void;
}

/**
 * Where there is no provider — the sign-in page, before anybody is somebody — a change is
 * applied and kept by the browser as before, and remembered by nobody.
 */
const NOWHERE: Preferences = {
  current: {
    defaultView: null,
    language: null,
    theme: null,
    deskWidth: null,
    showOnBreak: null,
    entrances: null,
    numbers: null,
  },
  access: DESK_ONLY,
  save: () => undefined,
};

export const PreferencesContext = createContext<Preferences>(NOWHERE);

export function usePreferences(): Preferences {
  return useContext(PreferencesContext);
}
